import { useState, useEffect, useCallback } from 'react'
import { api } from '../utils/api'

// ── §88: Job Progress Toast ───────────────────────────────────────────────────
// Shows progress of a long-running AI job (BullMQ) in the bottom-right corner.
// Parent passes jobId; component polls until job is done/failed.

interface JobStatus {
  jobId: string
  state: 'waiting' | 'active' | 'completed' | 'failed' | 'delayed'
  progress: number
  failedReason: string | null
}

interface Props {
  jobId: string
  label?: string
  onCompleted?: (jobId: string) => void
  onFailed?: (jobId: string, reason: string) => void
  onDismiss?: () => void
}

const STATE_LABEL: Record<string, string> = {
  waiting: 'Ожидание...',
  active: 'Выполняется...',
  delayed: 'В очереди...',
  completed: 'Готово!',
  failed: 'Ошибка',
}

export function JobProgressToast({ jobId, label, onCompleted, onFailed, onDismiss }: Props) {
  const [status, setStatus] = useState<JobStatus | null>(null)
  const [dismissed, setDismissed] = useState(false)

  const poll = useCallback(async () => {
    try {
      const s = await api.get<JobStatus>(`/ai/job/${jobId}/status`)
      setStatus(s)
      if (s.state === 'completed') {
        onCompleted?.(jobId)
        setTimeout(() => setDismissed(true), 3000)
      } else if (s.state === 'failed') {
        onFailed?.(jobId, s.failedReason ?? 'Неизвестная ошибка')
      }
    } catch {
      // ignore polling errors
    }
  }, [jobId, onCompleted, onFailed])

  useEffect(() => {
    void poll()
    const interval = setInterval(() => {
      if (status?.state === 'completed' || status?.state === 'failed') {
        clearInterval(interval)
        return
      }
      void poll()
    }, 2000)
    return () => clearInterval(interval)
  }, [poll, status?.state])

  if (dismissed) return null

  const isCompleted = status?.state === 'completed'
  const isFailed = status?.state === 'failed'
  const progress = status?.progress ?? 0

  return (
    <div style={{
      position: 'fixed', bottom: 24, right: 24, zIndex: 10000,
      width: 300, background: 'var(--bg-1)', border: `1px solid ${isFailed ? '#ef4444' : isCompleted ? '#22c55e' : 'var(--border)'}`,
      borderRadius: 12, boxShadow: '0 8px 32px rgba(0,0,0,0.2)',
      overflow: 'hidden', transition: 'all 0.2s',
    }}>
      {/* Progress bar */}
      <div style={{ height: 3, background: 'var(--bg-2)', position: 'relative' }}>
        <div style={{
          position: 'absolute', left: 0, top: 0, height: '100%',
          width: isCompleted ? '100%' : `${progress}%`,
          background: isFailed ? '#ef4444' : isCompleted ? '#22c55e' : 'var(--accent)',
          transition: 'width 0.3s ease',
        }} />
      </div>

      <div style={{ padding: '12px 14px', display: 'flex', alignItems: 'center', gap: 10 }}>
        <div style={{ fontSize: 20, flexShrink: 0 }}>
          {isFailed ? '❌' : isCompleted ? '✅' : '⏳'}
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--text-primary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {label ?? 'AI задача'}
          </div>
          <div style={{ fontSize: 11, color: isFailed ? '#ef4444' : isCompleted ? '#22c55e' : 'var(--text-muted)', marginTop: 2 }}>
            {isFailed ? (status?.failedReason?.slice(0, 60) ?? 'Ошибка') : STATE_LABEL[status?.state ?? 'waiting']}
            {!isFailed && !isCompleted && progress > 0 ? ` ${progress}%` : ''}
          </div>
        </div>
        <button
          onClick={() => { setDismissed(true); onDismiss?.() }}
          style={{ background: 'none', border: 'none', color: 'var(--text-muted)', fontSize: 16, cursor: 'pointer', padding: 0, lineHeight: 1, flexShrink: 0 }}
        >×</button>
      </div>
    </div>
  )
}

// ── Job progress manager — tracks multiple jobs at once ───────────────────────

interface TrackedJob { jobId: string; label: string }

export function useJobTracker() {
  const [jobs, setJobs] = useState<TrackedJob[]>([])

  function trackJob(jobId: string, label: string) {
    setJobs(prev => [...prev.filter(j => j.jobId !== jobId), { jobId, label }])
  }

  function removeJob(jobId: string) {
    setJobs(prev => prev.filter(j => j.jobId !== jobId))
  }

  return { jobs, trackJob, removeJob }
}
